import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle
} from 'react';
import LinkPreviewTooltip from './LinkPreviewTooltip';
import { isUrl, isSafeUrl } from '../../utils/linkUnfurling';

/**
 * Wrapper component that adds link preview functionality to any HTML content
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to wrap
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.enabled - Whether link previews are enabled
 * @param {React.Ref} ref - Forwarded ref
 */
const LinkPreviewWrapper = forwardRef(({
  children,
  className = '',
  enabled = true,
  ...otherProps
}, ref) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const wrapperRef = useRef(null);

  // Allow parent to access the internal DOM element
  useImperativeHandle(ref, () => wrapperRef.current);

  useEffect(() => {
    if (!enabled || !wrapperRef.current) return;

    const wrapper = wrapperRef.current;

    const handleMouseOver = (event) => {
      let linkElement = event.target;
      if (linkElement.tagName !== 'A') {
        linkElement = linkElement.closest('a');
      }

      if (
        linkElement &&
        linkElement.href &&
        isUrl(linkElement.href) &&
        isSafeUrl(linkElement.href) &&
        linkElement.href !== previewUrl
      ) {
        if (hoverTimeout) clearTimeout(hoverTimeout);
        const timeout = setTimeout(() => {
          setPreviewUrl(linkElement.href);
          setPreviewTarget(linkElement);
        }, 200); // Delay before showing preview
        setHoverTimeout(timeout);
      }
    };

    const handleMouseOut = (event) => {
      let linkElement = event?.target;
      if (linkElement.tagName !== 'A') {
        linkElement = linkElement.closest('a');
      }

      const relatedTarget = event?.relatedTarget;
      const isMovingToTooltip = relatedTarget?.closest('.link-preview-tooltip');
      const isStayingInLink = relatedTarget && linkElement?.contains(relatedTarget);

      if (linkElement && !isMovingToTooltip && !isStayingInLink) {
        if (hoverTimeout) {
          clearTimeout(hoverTimeout);
          setHoverTimeout(null);
        }

        setTimeout(() => {
          const tooltipElement = document.querySelector('.link-preview-tooltip:hover');
          if (!tooltipElement) {
            setPreviewUrl(null);
            setPreviewTarget(null);
          }
        }, 300);
      }
    };

    const handleClick = () => {
      setPreviewUrl(null);
      setPreviewTarget(null);
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
    };

    wrapper.addEventListener('mouseover', handleMouseOver);
    wrapper.addEventListener('mouseout', handleMouseOut);
    wrapper.addEventListener('click', handleClick);

    return () => {
      wrapper.removeEventListener('mouseover', handleMouseOver);
      wrapper.removeEventListener('mouseout', handleMouseOut);
      wrapper.removeEventListener('click', handleClick);
      if (hoverTimeout) clearTimeout(hoverTimeout);
    };
  }, [enabled, hoverTimeout, previewUrl]);

  const handleClosePreview = () => {
    setPreviewUrl(null);
    setPreviewTarget(null);
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  return (
    <>
      <div
        ref={wrapperRef}
        className={`link-preview-wrapper ${className}`}
        {...otherProps}
      >
        {children}
      </div>

      {enabled && previewUrl && previewTarget && (
        <LinkPreviewTooltip
          url={previewUrl}
          targetElement={previewTarget}
          onClose={handleClosePreview}
          position="bottom"
        />
      )}
    </>
  );
});

export default LinkPreviewWrapper;
